import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { NavArrowRight } from 'iconoir-react'
import { ComponentProps, CSSProperties, Fragment, ReactElement, ReactNode, useState } from 'react'
import { Badge } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import type { ReleaseCard, ReleaseGap, ReleaseGroups } from 'app/types/feature-toggle-release'
import { releaseTogglesToGive } from 'lib/featureToggleReleaseGroups'
import { AddOverrideModal } from './AddOverrideModal'
import type { FeatureRow } from './featureToggleModel'
import { FeatureToggleTable } from './FeatureToggleTable'
import { OverrideName } from './OverrideName'
import { useToggleTableState } from './useToggleTableState'

type TableProps = ComponentProps<typeof FeatureToggleTable>

type HandlerProps = Omit<TableProps, `rows` | `rowNote` | `isRowHighlighted`>

type GiveRelease = (rows: FeatureRow[], userIds: string[]) => void | Promise<void>

type Props = Omit<HandlerProps, `rowState`> & { groups: ReleaseGroups; onGiveRelease: GiveRelease }

const MAX_NAMED_GAPS = 2

function GapNote({ gap }: { gap: ReleaseGap }): ReactElement {
  if (gap.everyone) return <p className="mt-0.5 text-xs text-warning-strong">Not on for everyone</p>
  const targets = [
    ...gap.workspaceIds.map((id) => ({ kind: `Workspace` as const, id })),
    ...gap.userIds.map((id) => ({ kind: `User` as const, id })),
  ]
  const named = targets.slice(0, MAX_NAMED_GAPS)
  const more = targets.length - named.length
  return (
    <p className="mt-0.5 text-xs text-warning-strong">
      Missing for{` `}
      {named.map((target, i) => (
        <Fragment key={`${target.kind}-${target.id}`}>
          {i > 0 && `, `}
          <OverrideName kind={target.kind} id={target.id} />
        </Fragment>
      ))}
      {more > 0 && ` +${more} more`}
    </p>
  )
}

function SectionHeader({
  label,
  count,
  open,
  onToggle,
}: {
  label: string
  count: number
  open: boolean
  onToggle: () => void
}): ReactElement {
  return (
    <Button variant="ghost" size="inline" aria-expanded={open} onClick={onToggle} className="mb-2">
      <NavArrowRight className={classNames(`h-3.5 w-3.5 text-subtle transition-transform`, open && `rotate-90`)} />
      <span className="text-body-sm font-semibold tracking-tight text-strong">{label}</span>
      <span className="text-label-xs font-medium tabular-nums text-minimal">{count}</span>
    </Button>
  )
}

function ReleaseCardView({
  card,
  handlers,
  onGiveRelease,
}: {
  card: ReleaseCard
  handlers: HandlerProps
  onGiveRelease: GiveRelease
}): ReactElement {
  const [giveOpen, setGiveOpen] = useState(false)
  const gapByName = new Map(card.toggles.map((t) => [t.row.name, t.gap]))
  const total = card.toggles.length
  const rows = card.toggles.map((t) => t.row)
  const canGive = rows.some((row) => !row.archived && row.state !== `global`)
  const usersWithEverything = new Set(
    rows
      .flatMap((row) => row.userOverrides.map((o) => o.typeId))
      .filter((id): id is string => !!id && releaseTogglesToGive(rows, id).length === 0),
  )
  const rowNote = (row: TableProps[`rows`][number]): ReactNode => {
    const gap = gapByName.get(row.name)
    return gap ? <GapNote gap={gap} /> : null
  }
  return (
    <div role="group" aria-label={card.release.name} className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-strong">{card.release.name}</span>
            {card.release.epics.map((epic) => (
              <Badge key={epic} label={epic} />
            ))}
          </div>
          <p className="text-xs text-subtle">{card.release.description}</p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-subtle">
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={card.onCount}
            className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-muted"
          >
            <div
              className="h-full w-(--release-progress) rounded-full bg-primary-600"
              style={{ '--release-progress': `${total ? (card.onCount / total) * 100 : 0}%` } as CSSProperties}
            />
          </div>
          <span className="tabular-nums">{`${card.onCount} of ${total} on`}</span>
          {canGive && (
            <Button size="xs" variant="tertiary" onClick={() => setGiveOpen(true)}>
              Give to users
            </Button>
          )}
        </div>
        <AddOverrideModal
          open={giveOpen}
          onOpenChange={setGiveOpen}
          kind="User"
          existingIds={usersWithEverything}
          title={`Give ${card.release.name} to users`}
          confirmLabel="Give"
          onConfirm={(ids) => onGiveRelease(rows, ids)}
        />
      </div>
      {total === 0 ? (
        <p className="rounded-md border border-default bg-surface px-4 py-3 text-xs text-minimal">No toggles yet</p>
      ) : (
        <FeatureToggleTable
          rows={rows}
          rowNote={rowNote}
          isRowHighlighted={(row) => !!gapByName.get(row.name)}
          {...handlers}
        />
      )}
    </div>
  )
}

export const ByReleaseView = observer(function ByReleaseView({
  groups,
  onGiveRelease,
  ...handlers
}: Props): ReactElement {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set([`released`]))
  const rowState = useToggleTableState()
  const sharedProps = { ...handlers, rowState }

  const toggleSection = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <div className="space-y-6">
      <ReleaseSection
        label="In progress"
        cards={groups.inProgress}
        open={!collapsed.has(`in-progress`)}
        onToggle={() => toggleSection(`in-progress`)}
        handlers={sharedProps}
        onGiveRelease={onGiveRelease}
      />

      {groups.unassigned.length > 0 && (
        <section>
          <SectionHeader
            label="No release"
            count={groups.unassigned.length}
            open={!collapsed.has(`unassigned`)}
            onToggle={() => toggleSection(`unassigned`)}
          />
          {!collapsed.has(`unassigned`) && <FeatureToggleTable rows={groups.unassigned} {...sharedProps} />}
        </section>
      )}

      <ReleaseSection
        label="Released"
        cards={groups.released}
        open={!collapsed.has(`released`)}
        onToggle={() => toggleSection(`released`)}
        handlers={sharedProps}
        onGiveRelease={onGiveRelease}
      />
    </div>
  )
})

function ReleaseSection({
  label,
  cards,
  open,
  onToggle,
  handlers,
  onGiveRelease,
}: {
  label: string
  cards: ReleaseCard[]
  open: boolean
  onToggle: () => void
  handlers: HandlerProps
  onGiveRelease: GiveRelease
}): ReactElement | null {
  if (cards.length === 0) return null
  return (
    <section>
      <SectionHeader label={label} count={cards.length} open={open} onToggle={onToggle} />
      {open && (
        <div className="space-y-5">
          {cards.map((card) => (
            <ReleaseCardView key={card.slug} card={card} handlers={handlers} onGiveRelease={onGiveRelease} />
          ))}
        </div>
      )}
    </section>
  )
}
